import React, { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const TermsAndConditions: React.FC = () => {
  useEffect(() => {
    document.title = "Terms & Conditions | Exeter Flow Screeds";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        "content",
        "Terms and Conditions for liquid screed supply and installation services by Exeter Flow Screeds Ltd across Exeter, Devon & Somerset.",
      );

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute(
        "href",
        "https://exeterflowscreed.co.uk/terms-and-conditions",
      );

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl)
      ogUrl.setAttribute(
        "content",
        "https://exeterflowscreed.co.uk/terms-and-conditions",
      );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#080810] text-stone-300 font-sans flex flex-col justify-between">
      <Navbar />

      <main className="pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
        <div className="mb-10 pb-8 border-b border-stone-800">
          <p className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase mb-3">
            LEGAL INFORMATION
          </p>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Terms & Conditions
          </h1>
          <p className="text-stone-400 text-sm">Last updated: 23 August 2026</p>
        </div>

        <div className="space-y-8 text-stone-300 leading-relaxed text-sm sm:text-base">
          <section className="space-y-4">
            <p>
              These Terms and Conditions govern the provision of liquid screed
              supply, installation, and floor preparation services by{" "}
              <strong className="text-white">Exeter Flow Screeds Ltd</strong>{" "}
              ("Company", "we", "us", "our") to domestic and commercial clients
              ("Client", "you").
            </p>
            <p>
              By requesting a quote, confirming a booking, or accepting a
              service from Exeter Flow Screeds Ltd, you agree to be bound by
              these terms.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              1. Quotations & Pricing
            </h2>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                All quotations provided by Exeter Flow Screeds Ltd are based on
                site measurements, drawings, or client-provided floor dimensions
                and screed depth requirements.
              </li>
              <li>
                Quotations remain valid for 30 days from the date of issue
                unless specified otherwise in writing.
              </li>
              <li>
                If site dimensions or required screed depth differ from the
                agreed quotation at the time of pour, we reserve the right to
                adjust final pricing to reflect additional material or labor.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              2. Site Preparation & Access
            </h2>
            <p>
              To ensure a successful screed pour, the Client is responsible for
              ensuring the following prior to installation team arrival:
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                <strong className="text-stone-200">
                  Building Weatherproofing:
                </strong>{" "}
                The building envelope must be fully watertight (roof, doors,
                windows, or temporary protection installed).
              </li>
              <li>
                <strong className="text-stone-200">
                  Subfloor & Insulation:
                </strong>{" "}
                Insulation boards must be fitted flat and securely pinned down.
                DPM (damp-proof membrane) or tanking layers must be taped
                watertight at joints and perimeter edges to prevent screed
                leakage.
              </li>
              <li>
                <strong className="text-stone-200">Underfloor Heating:</strong>{" "}
                Underfloor heating pipes must be clipped or pinned securely at
                maximum 300mm intervals and pressure tested prior to pouring.
              </li>
              <li>
                <strong className="text-stone-200">
                  Vehicle Access & Washout:
                </strong>{" "}
                Safe, hard-surface access for ready-mix concrete trucks (approx.
                20-30 tonnes) and pump equipment, plus a designated area for
                equipment washdown.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              3. Installation & Tolerances
            </h2>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                All Cemfloor liquid screeds are installed using laser-level
                equipment to SR1 laser tolerances under standard site
                conditions.
              </li>
              <li>
                Extremely harsh weather conditions, unsealed draughts, or
                inadequate building protection post-pour may affect curing
                aesthetics, for which the Company cannot be held liable.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              4. Curing & Post-Pour Care
            </h2>
            <ul className="list-disc list-inside space-y-2 text-stone-400 pl-2">
              <li>
                Walk-on times are typically 24 to 48 hours post-installation
                depending on ambient site temperatures.
              </li>
              <li>
                Windows and doors should remain closed for the first 24 to 48
                hours to prevent rapid surface drying, and then opened for
                ventilation thereafter.
              </li>
              <li>
                Final drying times vary based on screed thickness, moisture
                levels, and site ventilation. High humidity or cold weather will
                extend drying times.
              </li>
            </ul>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              5. Guarantees & Warranties
            </h2>
            <p>
              We provide a{" "}
              <strong className="text-white">
                10-year Cemfloor product guarantee
              </strong>{" "}
              on Cemfloor liquid screed materials installed by our certified
              team. This guarantee covers the structural integrity of the
              Cemfloor product itself under standard usage when installed on
              correctly prepared subfloors. Workmanship is guaranteed separately
              for the duration agreed in your quotation.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              6. Payment Terms
            </h2>
            <p>
              Payment terms will be specified on your formal quotation. Unless
              agreed otherwise in writing, full payment or remaining balance is
              due upon satisfactory completion of the screed pour.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              7. Limitation of Liability
            </h2>
            <p>
              Exeter Flow Screeds Ltd shall not be liable for site delays caused
              by severe weather, ready-mix batch plant breakdown, blocked access
              routes, or third-party trades.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              8. Governing Law
            </h2>
            <p>
              These Terms and Conditions shall be governed by and construed in
              accordance with the laws of England and Wales.
            </p>
          </section>

          <section className="space-y-4 pt-4 border-t border-stone-800/80">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              9. Contact Us
            </h2>
            <div className="bg-[#12121D] p-5 rounded-xl border border-stone-800 space-y-2 text-stone-300">
              <p>
                <strong className="text-white">Exeter Flow Screeds Ltd</strong>
              </p>
              <p>
                Email:{" "}
                <a
                  href="mailto:efscreed@gmail.com"
                  className="text-[#C9A84C] hover:underline"
                >
                  efscreed@gmail.com
                </a>
              </p>
              <p>
                Phone:{" "}
                <a
                  href="tel:07765712609"
                  className="text-[#C9A84C] hover:underline"
                >
                  07765 712609
                </a>{" "}
                /{" "}
                <a
                  href="tel:07470861869"
                  className="text-[#C9A84C] hover:underline"
                >
                  07470 861869
                </a>
              </p>
              <p>Address: Turner Cl, Newton Poppleford, Sidmouth EX10 0EN</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
